import { lazyWithPreload } from "#hooks/lazyWithPreload";
// eslint-disable-next-line no-restricted-imports
import { IconButton, TextField, type TextFieldProps } from "@mui/material";
import { useMemo, forwardRef, useState, useEffect } from "react";

const VisibilityOffIconLoader = () =>
  import("@mui/icons-material/VisibilityOff");
const VisibilityIconLoader = () => import("@mui/icons-material/Visibility");

const VisibilityIcon = lazyWithPreload(VisibilityIconLoader);
const VisibilityOffIcon = lazyWithPreload(VisibilityOffIconLoader);

export type InputProps = TextFieldProps & {
  errorText?: string;
};

export const Input = forwardRef<any, InputProps>(
  (
    {
      variant = "outlined",
      fullWidth = true,
      errorText,
      helperText: userHelperText,
      label: userLabel,
      required,
      value: userValue,
      children,
      type: userType,
      slotProps,
      ...rest
    },
    ref
  ) => {
    const [type, setType] = useState(() =>
      userType === "number" ? "text" : userType || "text"
    );
    const helperText = useMemo(
      () => (rest.error ? errorText || userHelperText : userHelperText),
      [errorText, userHelperText, rest.error]
    );
    const label = useMemo(
      () => (userLabel ? userLabel + (required ? " *" : "") : ""),
      [userLabel, required]
    );

    const value = useMemo(() => userValue ?? "", [userValue]);

    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
      setType(userType === "number" ? "text" : userType || "text");
      if (userType === "password") {
        VisibilityIcon.preload();
        VisibilityOffIcon.preload();
      }
    }, [userType]);

    return (
      <TextField
        {...rest}
        label={label}
        value={value}
        helperText={helperText}
        variant={variant}
        fullWidth={fullWidth}
        ref={ref}
        type={showPassword ? "text" : type}
        inputMode={
          rest.inputMode || (userType === "number" ? "numeric" : undefined)
        }
        slotProps={{
          ...(slotProps ?? {}),
          htmlInput: {
            inputMode:
              rest.inputMode || (userType === "number" ? "numeric" : undefined),
            ...slotProps?.htmlInput,
          },
          input: {
            ...slotProps?.input,
            ...rest.InputProps,
            endAdornment: (
              <>
                {userType === "password" ? (
                  <IconButton
                    onClick={() => setShowPassword((old) => !old)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {!showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                ) : undefined}
                {(slotProps?.input as any)?.endAdornment}
                {rest.InputProps?.endAdornment}
              </>
            ),
          },
        }}
      >
        {children}
      </TextField>
    );
  }
);
